import { LegacyRef, ReactNode, useCallback, useState } from 'react';
import { useComponentVisible } from '../../model/hook/ClickOutside';

interface IInputConfig {
	className: string;
	placeholder: string;
	autoComplete?: string | undefined;
	id?: string | undefined;
}

const DefaultInputConfig: IInputConfig = {
	className: '',
	placeholder: '',
	autoComplete: ''
};

interface IWrapperConfig {
	className: string;
}

const DefaultWrapperConfig: IWrapperConfig = {
	className: ''
};

interface IProps {
	originalInputId?: string | null;
	originalInput?: HTMLInputElement;
	inputConfig: IInputConfig;
	wrapperConfig?: IWrapperConfig;
	onSearch?: (search: string, setList: (list: any) => void) => void;
	formatItem?: (item: any, defaultOnClick: (item: string) => void) => ReactNode;
	defaultValue: string;
	initialList?: Array<any>;
	dispatchChangeOnBlur?: boolean;
}

const _SuperSelect = ({
	originalInput,
	inputConfig = DefaultInputConfig,
	wrapperConfig = DefaultWrapperConfig,
	onSearch,
	formatItem,
	defaultValue = '',
	initialList = [],
	dispatchChangeOnBlur = false
}: IProps) => {
	const setValueToOriginalInput = useCallback(
		(value: string, dispatchOnchange: boolean = false, submitForm: boolean = false) => {
			if (originalInput) {
				originalInput.value = value;
				if (dispatchOnchange) {
					originalInput.dispatchEvent(new Event('change'));
				}

				if (submitForm) {
					originalInput.form?.submit();
				}
			}
		},
		[originalInput]
	);

	const onBlur = useCallback(
		(e: any) => {
			// setTimeout is because of document.activeElement
			setTimeout(() => {
				if (!dispatchChangeOnBlur) {
					return;
				}

				const element = e.target.tagName === 'INPUT' ? e.target.parentNode : e.target;
				if (element && !element.contains(document.activeElement)) {
					setValueToOriginalInput(e.target.value, true);
				}
			}, 0);
		},
		[setValueToOriginalInput, dispatchChangeOnBlur]
	);

	const onFocus = useCallback((e: any) => {
		const input = e.target.tagName === 'INPUT' ? e.target.parentNode : e.target;

		setTimeout(() => {
			const offsetTop = input.getBoundingClientRect().top + window.scrollY;

			window.scrollTo({
				top: offsetTop - 30,
				behavior: 'smooth'
			});
		}, 100);
	}, []);

	const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
	const [list, setList] = useState(initialList);
	const [value, setValue] = useState(defaultValue);

	const onSearchInternal = useCallback(
		(e: any) => {
			setValue(e.target.value);
			setValueToOriginalInput(e.target.value);

			if (onSearch === null || onSearch === undefined) {
				return;
			} else {
				onSearch(e.target.value, setList);
			}
		},
		[onSearch, setValueToOriginalInput]
	);

	const defaultOnClick = (item: string) => {
		setValue(item);
		setValueToOriginalInput(item, true);
		setIsComponentVisible(false);
	};

	const renderItems = () =>
		list.map((item: string, index: number) => (
			<li key={index} tabIndex={0}>
				{formatItem ? (
					formatItem(item, defaultOnClick)
				) : (
					// eslint-disable-next-line react/jsx-no-bind
					<a className="dropdown-item" onClick={() => defaultOnClick(item)}>
						{item}
					</a>
				)}
			</li>
		));

	return (
		<div
			className={'super-select dropdown ' + wrapperConfig.className}
			ref={ref as LegacyRef<HTMLDivElement>}
			onBlur={onBlur}
			tabIndex={-1}>
			<input
				{...inputConfig}
				onChange={onSearchInternal}
				onFocus={onFocus}
				onTouchStart={onFocus}
				value={value}
				data-id={originalInput?.id}
				tabIndex={0}
				onKeyUp={(e) => {
					if (e.key === 'Enter') {
						setValueToOriginalInput(value, true, true);
					}
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
					}
				}}
			/>
			{list.length > 0 && isComponentVisible ? <ul className="dropdown-menu show">{renderItems()}</ul> : null}
		</div>
	);
};

export const SuperSelect = (props: IProps) => {
	const newProps = { ...props };
	if (props.originalInputId) {
		const originalInput = document.getElementById(props.originalInputId);
		if (originalInput) {
			originalInput.style.display = 'none';
			newProps.originalInput = originalInput as unknown as HTMLInputElement;
		}
	}

	return <_SuperSelect {...newProps} />;
};

export const SuperSelectDivider = () => (
	<a className={'dropdown-item dropdown-item-divider'}>
		<hr />
	</a>
);
